import React from 'react'

import { Table, TableCol, TableHead, TableRow } from '../../../../../../components/pdf/Table'
import Text from '../../../../../../components/pdf/Text'
import { HEADING_COLOR, TABLE_BORDER_RED, TABLE_STRIPE_TINT } from '../../_helpers/brand'

/**
 * Tabelas "em grade" do PGR Bradesco (bordas vermelhas, cabeçalho vermelho sólido com
 * texto branco, linhas ímpares com um leve realce vermelho-claro) — o padrão visual
 * confirmado em praticamente toda tabela do modelo real ("Modelo PGR 2026 - Banco
 * Bradesco_Varejo"), por exemplo "1.2. Identificação das Dependências", a Matriz de
 * Risco, o Inventário de Riscos e o Plano de Ação.
 *
 * Local ao módulo Bradesco (não em `components/pdf/`) porque o estilo — bordas e
 * cabeçalho vermelhos — é específico deste cliente; os componentes genéricos
 * `components/pdf/Table.js` e `TableHelpers.js` (usados por outras marcas) não são
 * alterados. Estes wrappers só adicionam cor/estilo — nenhum deles toca em dados,
 * cálculo ou nos vetores de `_helpers/riskEngine.js` / `_helpers/cargoEngine.js`.
 */

// Tabela com borda vermelha (substitui `components/pdf/Table` nas seções do Bradesco).
export const RedTable = ({ children, ...props }) => (
  <Table color={TABLE_BORDER_RED} {...props}>
    {children}
  </Table>
)

// Linha de dados com borda vermelha. `striped` aplica o leve realce vermelho-claro das
// linhas ímpares observado no modelo (ex.: tabela "Critérios de Gradação da Probabilidade").
export const RedRow = ({ striped, style, children, ...props }) => (
  <TableRow color={TABLE_BORDER_RED} style={{ ...(striped ? { backgroundColor: TABLE_STRIPE_TINT } : {}), ...style }} {...props}>
    {children}
  </TableRow>
)

// Célula de cabeçalho: preenchimento vermelho sólido e texto branco em negrito por
// padrão (o cabeçalho de tabela em grade do modelo, ex.: "EMPRESA | JUNÇÃO | ..."). Ao
// passar um `bgColor` claro (ex.: branco — usado na Matriz de Risco para o sub-cabeçalho
// de colunas/linhas), o texto passa a preto automaticamente, como no modelo.
export const RedHeadCell = ({ children, align = 'center', colSpan, rowSpan, width, bgColor, size = 'small' }) => {
  const isLightFill = bgColor === '#FFFFFF' || bgColor === '#fff' || bgColor === 'white'
  return (
    <TableHead color={TABLE_BORDER_RED} bgColor={bgColor || TABLE_BORDER_RED} colSpan={colSpan} rowSpan={rowSpan} width={width}>
      <Text bold color={isLightFill ? '#000000' : '#FFFFFF'} align={align} size={size}>
        {children}
      </Text>
    </TableHead>
  )
}

// Célula de dado: borda vermelha, texto preto (cor padrão do documento) — `bgColor`
// permite o preenchimento colorido da Matriz de Risco (heat-map por classificação).
export const RedDataCell = ({
  children,
  align = 'left',
  colSpan,
  rowSpan,
  bold,
  bgColor,
  size = 'small',
  width,
  style,
  preLine
}) => (
  <TableCol color={TABLE_BORDER_RED} bgColor={bgColor} colSpan={colSpan} rowSpan={rowSpan} width={width} style={style}>
    <Text align={align} bold={bold} size={size} preLine={preLine}>
      {children}
    </Text>
  </TableCol>
)

// Linha "rótulo: valor" (ex.: "1.1. Identificação da Unidade") — rótulo no mesmo azul
// petróleo dos títulos de seção (`HEADING_COLOR`, confirmado por inspeção pixel-a-pixel do
// modelo real — ver histórico em `_helpers/brand.js`), valor em preto, divisórias
// horizontais vermelhas. Substitui `TableRowLabelValue` (components/pdf/TableHelpers.js)
// apenas dentro do módulo Bradesco, porque aquele componente compartilhado não permite
// colorir o rótulo.
export const LabelValueRow = ({ label, value, children, colSpan, valueColSpan }) => (
  <TableRow color={TABLE_BORDER_RED}>
    <TableCol color={TABLE_BORDER_RED} colSpan={colSpan}>
      <Text bold color={HEADING_COLOR}>
        {label}:
      </Text>
    </TableCol>
    <TableCol color={TABLE_BORDER_RED} colSpan={valueColSpan}>
      {children || <Text>{value}</Text>}
    </TableCol>
  </TableRow>
)
